import { SelectBox } from 'devextreme-react';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useAuth } from '../../../auth';
import { getAddressesByZone, lastScanAddressByUser, resetAddress } from '../../../services/sayim/sayim-api';
import GeriButonu from '../../common/GeriButonu';
import ResetAddressModal from './ResetAddressModal';

const SayimAdresSecimi = ({ selectedCount, loading, setLoading, setCurrentScreen, onZoneAndAddressSelect, selectedZone: propSelectedZone }) => {
    const [selectedZone, setSelectedZone] = useState(propSelectedZone);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [lastScanAddress, setLastScanAddress] = useState(null);
    const [lastScanLoading, setLastScanLoading] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoType, setInfoType] = useState('');
    const { hasRole } = useAuth(30);

    // Component mount olduğunda veya propSelectedZone değiştiğinde adresleri yükle
    useEffect(() => {
        if (propSelectedZone && selectedCount) {
            setSelectedZone(propSelectedZone);
            loadAddressesForZone(propSelectedZone);
        } else if (selectedCount && selectedCount.zones && selectedCount.zones.length === 1) {
            // Eğer sadece 1 bölge varsa otomatik seç
            const singleZone = selectedCount.zones[0];
            setSelectedZone(singleZone);
            loadAddressesForZone(singleZone);
        }
    }, [propSelectedZone, selectedCount]);

    // Son adres bilgisini yükle
    useEffect(() => {
        if (selectedCount) {
            loadLastScanAddress();
        }
    }, [selectedCount]);

    // Son adres bilgisini yükleyen fonksiyon
    const loadLastScanAddress = async () => {
        if (!selectedCount) return;

        setLastScanLoading(true);
        try {
            const userId = parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'));
            const response = await lastScanAddressByUser(selectedCount.count_code, userId);

            if (response.success && response.data) {
                setLastScanAddress(response.data);
            } else {
                setLastScanAddress(null);
            }
        } catch (error) {
            console.error('Son adres bilgisi alınırken hata:', error);
            setLastScanAddress(null);
        } finally {
            setLastScanLoading(false);
        }
    };

    // Adresleri yükleyen fonksiyon
    const loadAddressesForZone = async (zone) => {
        if (!zone || !selectedCount) {
            console.log('Zone veya selectedCount eksik:', { zone, selectedCount });
            return;
        }


        setAddressesLoading(true);
        setSelectedAddress(null);

        try {
            const addressData = await getAddressesByZone(selectedCount.count_code, zone.zone_id);

            let result = _.orderBy(addressData.addresses, [
                (item) => item.coordinate + item.direction, // Önce coordinate + direction kombinasyonuna göre
                'row_number' // Sonra row_number'a göre
            ], ['asc', 'asc']);
            setAddresses(result || []);
        } catch (error) {
            console.error('Adres bilgileri alınırken hata:', error);
            setAddresses([]);
        } finally {
            setAddressesLoading(false);
        }
    };

    // Enter tuşu event handler'ı
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && selectedAddress) {
            e.preventDefault();
            onZoneAndAddressSelect(selectedZone, selectedAddress);
        }
    };

    // Zone seçildiğinde adresleri getir
    const handleZoneSelect = async (e) => {
        const zoneId = e.value;
        const zone = selectedCount.zones.find(z => z.zone_id === zoneId);

        if (zone) {
            setSelectedZone(zone);
            await loadAddressesForZone(zone);
        } else {
            setSelectedZone(null);
            setAddresses([]);
            setSelectedAddress(null);
        }
    };

    // Adres seçildiğinde
    const handleAddressSelect = (e) => {
        const address = e.value;
        setSelectedAddress(address);
    };

    // Reset işlemi
    const handleReset = async () => {
        try {
            setLoading(true);

            // API çağrısı
            const addressCode = `${selectedAddress.coordinate}${selectedAddress.direction}-${selectedAddress.row_number}`;
            await resetAddress(addressCode, selectedCount.count_code, selectedZone.zone_id);

            // Başarılı reset sonrası state güncelleme
            setSelectedAddress(null);
            setAddresses([]);
            setSelectedZone(null);
            setShowResetModal(false);

            // Adresleri yeniden yükle
            await loadAddressesForZone(selectedZone);

        } catch (error) {
            console.error('Reset işlemi başarısız:', error);
            // Hata durumunda modal'ı kapat
            setShowResetModal(false);
        } finally {
            setLoading(false);
        }
    };

    // Reset modal'ını göster
    const handleShowResetModal = () => {
        setShowResetModal(true);
    };

    // Reset modal'ını kapat
    const handleCancelReset = () => {
        setShowResetModal(false);
    };

    // Info modal'ını aç
    const handleInfoClick = (type) => {
        setInfoType(type);
        setShowInfoModal(true);
    };

    // Info modal içeriğini al
    const getInfoContent = () => {
        switch (infoType) {
            case 'lastScan':
                return {
                    title: 'Son Okutma Bilgisi',
                    content: (
                        <div>
                            <p>Bu alan size daha önce yaptığınız son işlem bilgisini gösterir:</p>
                            <ul>
                                <li><strong>Hangi adreste son çalıştığınızı</strong> gösterir</li>
                                <li><strong>O adresteki sayımın tamamlanıp tamamlanmadığını</strong> belirtir</li>
                                <li><strong>Yeşil tik işareti</strong> varsa o adres tamamlanmış demektir</li>
                                <li><strong>Son işlem tarihini</strong> gösterir</li>
                            </ul>
                            <p style={{ marginTop: '10px', color: '#6c757d', fontSize: '14px' }}>
                                Bu bilgi sayesinde kaldığınız yerden devam edebilirsiniz.
                            </p>
                        </div>
                    )
                };

            case 'addressSelection':
                return {
                    title: 'Adres Seçimi',
                    content: (
                        <div>
                            <p>Bu alanda adres seçimi yapabilirsiniz:</p>
                            <ul>
                                <li><strong>Adres Listesi:</strong> Seçilen bölgedeki tüm adresler listelenir</li>
                                <li><strong>Adres Bilgileri:</strong> Her adres için şu bilgiler gösterilir:
                                    <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                                        <li><strong>Kapasite:</strong> O adrestin kapasitesi</li>
                                        <li><strong>Sayılan:</strong> O adreste kaç şase sayılmış</li>
                                        <li><strong>Olan:</strong> O adreste fiziksel olarak kaç araç var</li>
                                    </ul>
                                </li>
                                <li><strong>Tamamlanan Adresler:</strong> Yeşil tik işaretli adresler tamamlanmış adreslerdir</li>
                                <li><strong>Arama:</strong> Adres kutusuna yazarak adres arayabilirsiniz</li>
                                <li><strong>Sıralama:</strong> Adresler koordinat ve sıra numarasına göre sıralanır</li>
                            </ul>
                        </div>
                    )
                };

            case 'resetButton':
                return {
                    title: 'Adresi Resetle',
                    content: (
                        <div>
                            <p>Bu buton seçili adresi sıfırlar:</p>
                            <ul>
                                <li><strong>Ne yapar:</strong> O adresteki tüm sayım verilerini siler</li>
                                <li><strong>Hangi veriler silinir:</strong>
                                    <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                                        <li>Okutulan tüm şase numaraları</li>
                                        <li>Sayım sayıları</li>
                                        <li>Tamamlanma durumu</li>
                                    </ul>
                                </li>
                                <li><strong>Ne zaman kullanılır:</strong> Yanlış adres seçimi veya hatalı sayım durumunda</li>
                                <li><strong>Dikkat:</strong> Bu işlem geri alınamaz, dikkatli kullanın</li>
                            </ul>
                            <div style={{
                                backgroundColor: '#fff3cd',
                                padding: '10px',
                                borderRadius: '4px',
                                marginTop: '15px',
                                border: '1px solid #ffeaa7'
                            }}>
                                <small style={{ color: '#856404' }}>
                                    <i className="fa fa-exclamation-triangle mr-1"></i>
                                    <strong>Uyarı:</strong> Reset işlemi geri alınamaz. Sadece gerektiğinde kullanın.
                                </small>
                            </div>
                        </div>
                    )
                };

            default:
                return {
                    title: 'Bilgi',
                    content: <div>Bilgi bulunamadı.</div>
                };
        }
    };

    // Zone verilerini hazırla
    const zoneData = selectedCount?.zones || [];

    // Adres verilerini hazırla
    const addressData = addresses;

    return (
        <>
            <div className="container-fluid" onKeyPress={handleKeyPress} tabIndex={0}>
                <Row className="justify-content-center">
                    <Col className='mt-2'>

                        <Card>
                            <CardBody>
                                <GeriButonu onClick={() => setCurrentScreen('countInput')} />
                                {selectedCount && (
                                    <div>
                                        {/* Sayım Bilgileri */}
                                        <Row style={{ marginBottom: '5px' }}>
                                            <Col md="6">
                                                <div style={{ marginBottom: '5px' }}>
                                                    <strong>Sayım No:</strong>
                                                    <div style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        marginTop: '5px'
                                                    }}>
                                                        {selectedCount.count_code}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md="6">
                                                <div style={{ marginBottom: '5px' }}>
                                                    <strong>Depo:</strong>
                                                    <div style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        marginTop: '5px'
                                                    }}>
                                                        {selectedCount.warehouse_name}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Son Tarama Adresi Bilgisi */}
                                        {lastScanAddress && (
                                            <Row style={{ marginBottom: '10px' }}>
                                                <Col>
                                                    <div style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e9ecef'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <i className={`fa ${lastScanAddress.is_finished ? 'fa-check-circle' : 'fa-clock-o'} mr-2`}
                                                                    style={{ color: lastScanAddress.is_finished ? '#28a745' : '#6c757d', fontSize: '12px' }}></i>
                                                                <span style={{
                                                                    color: '#495057',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    Son Okutma Bilgisi
                                                                </span>
                                                                <Button
                                                                    color="link"
                                                                    size="sm"
                                                                    onClick={() => handleInfoClick('lastScan')}
                                                                    style={{
                                                                        padding: '0',
                                                                        marginLeft: '5px',
                                                                        color: '#007bff',
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    <i className="fa fa-question-circle-o"></i>
                                                                </Button>
                                                            </div>
                                                            {lastScanAddress.is_finished && (
                                                                <span style={{ color: '#28a745', fontSize: '11px', fontWeight: '600' }}>
                                                                    Tamamlandı
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#333', fontWeight: '600' }}>
                                                            {lastScanAddress.address_code}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                                            {lastScanAddress.zone_name} • {moment.utc(lastScanAddress.scan_date).format('DD.MM.YYYY HH:mm')}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}

                                        {lastScanLoading && (
                                            <Row style={{ marginBottom: '15px' }}>
                                                <Col>
                                                    <div style={{
                                                        padding: '10px 15px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        textAlign: 'center'
                                                    }}>
                                                        <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                                                            <span className="sr-only">Yükleniyor...</span>
                                                        </div>
                                                        <span style={{ fontSize: '14px', color: '#666' }}>Son adres bilgisi yükleniyor...</span>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Bölge Seçimi - DevExtreme SelectBox */}
                                        {selectedCount.zones && selectedCount.zones.length > 0 && (
                                            <div style={{ marginBottom: '5px' }}>
                                                <strong >Bölge Seçimi:</strong>
                                                {selectedCount.is_address_based === false ? (
                                                    <div style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#e9ecef',
                                                        borderRadius: '4px',
                                                        marginTop: '5px',
                                                        color: '#6c757d'
                                                    }}>
                                                        {selectedCount.zones.map(zone => zone.zone_name).join(', ')}
                                                    </div>
                                                ) : (
                                                    <SelectBox
                                                        dataSource={zoneData}
                                                        displayExpr="zone_name"
                                                        valueExpr="zone_id"
                                                        placeholder="Bölge seçiniz..."
                                                        value={selectedZone?.zone_id}
                                                        onValueChanged={handleZoneSelect}
                                                        showClearButton={true}
                                                        searchEnabled={false}
                                                        stylingMode="filled"
                                                        width="100%"
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Adres Seçimi - DevExtreme SelectBox */}
                                        {selectedZone && selectedCount.is_address_based !== false && (
                                            <div className='mt-3'>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                    <strong>
                                                        Adres Seçimi:
                                                    </strong>
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        onClick={() => handleInfoClick('addressSelection')}
                                                        style={{
                                                            padding: '0',
                                                            color: '#007bff',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        <i className="fa fa-question-circle-o"></i>
                                                    </Button>
                                                </div>

                                                {addressesLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="sr-only">Adresler yükleniyor...</span>
                                                        </div>
                                                        <div style={{ marginTop: '10px' }}>Adresler yükleniyor...</div>
                                                    </div>
                                                ) : addresses.length > 0 ? (
                                                    <SelectBox
                                                        dataSource={addressData}
                                                        value={selectedAddress}
                                                        onValueChanged={handleAddressSelect}
                                                        placeholder="Adres seçiniz veya yazınız..."
                                                        showClearButton={true}
                                                        searchEnabled={true}
                                                        searchMode="contains"
                                                        searchExpr={['address_code']}
                                                        stylingMode="filled"
                                                        className='sayim-address-select-box'
                                                        width="100%"
                                                        displayExpr={(item) => {
                                                            if (!item) return '';
                                                            return `${item.coordinate}${item.direction}-${item.row_number}`;
                                                        }}
                                                        itemRender={(item) => {
                                                            const isCompleted = item.is_scanned;

                                                            return (
                                                                <div style={{
                                                                    padding: '5px 6px',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    backgroundColor: isCompleted ? '#f8fff8' : 'white',
                                                                    borderLeft: isCompleted ? '4px solid #28a745' : '4px solid #e9ecef',
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    opacity: isCompleted ? 0.7 : 1
                                                                }}>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{
                                                                            fontWeight: '600',
                                                                            color: isCompleted ? '#28a745' : '#333',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            {item.coordinate}{item.direction}-{item.row_number} ({item.capacity})
                                                                        </div>
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            gap: '8px',
                                                                            fontSize: '12px',
                                                                            color: isCompleted ? '#6c757d' : '#666',
                                                                            marginTop: '2px'
                                                                        }}>
                                                                            <span>Sayılan: {item.scan_count}</span>
                                                                            <span>•</span>
                                                                            <span>Olan: {item.physical_chassis_count}</span>
                                                                        </div>
                                                                    </div>
                                                                    {isCompleted && (
                                                                        <div style={{
                                                                            // backgroundColor: '',
                                                                            color: '#28a745',
                                                                            // padding: '3px 8px',
                                                                            // borderRadius: '10px',
                                                                            fontSize: '13px',
                                                                            fontWeight: '600'
                                                                        }}>
                                                                            ✓
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        textAlign: 'center',
                                                        padding: '20px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px'
                                                    }}>
                                                        Bu bölgede adres bulunamadı.
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Seçim Özeti */}
                                        {(selectedAddress || selectedCount?.is_address_based === false) && (
                                            <div className='w-100'>
                                                {/* Sayım Tipi Bilgisi */}
                                                <div className="p-1" role="alert" style={{ marginBottom: '0px', color: 'gray' }}>
                                                    <i className="fa fa-info-circle mr-2"></i>
                                                    <strong>Sayım Tipi:</strong> {selectedCount?.is_address_based === false ? 'Adressiz Sayım' : 'Adresli Sayım'}
                                                </div>

                                                {selectedAddress && (
                                                    <div style={{
                                                        marginTop: '5px',
                                                        padding: '8px 12px',
                                                        backgroundColor: '#f8fff8',
                                                        borderLeft: '4px solid #28a745',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e9ecef'
                                                    }}>
                                                        <div style={{
                                                            fontWeight: '600',
                                                            color: '#28a745',
                                                            fontSize: '14px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            {selectedAddress.coordinate}{selectedAddress.direction}-{selectedAddress.row_number} ({selectedAddress.capacity})
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '8px',
                                                            fontSize: '12px',
                                                            color: '#666'
                                                        }}>
                                                            <span>Sayılan: {selectedAddress.scan_count}</span>
                                                            <span>•</span>
                                                            <span>Olan: {selectedAddress.physical_chassis_count}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                <Button className='mt-2 mx-auto w-100' color="success"
                                                    style={{ borderRadius: "2px" }}
                                                    onClick={() => onZoneAndAddressSelect(selectedZone, selectedAddress)}
                                                >
                                                    {/* <i class="fa fa-check mr-2" aria-hidden="true"></i> */}
                                                    <i className="fa fa-play mr-2" aria-hidden="true"></i>
                                                    Başla
                                                </Button>

                                                {selectedAddress && selectedCount?.is_address_based !== false && (
                                                    <div style={{
                                                        position: 'relative', marginBottom: '30px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '5px'
                                                    }}>
                                                        {
                                                            hasRole("sayim_reset_update") &&
                                                            <Button className='mt-2 mx-auto w-100' color="warning"
                                                                style={{ borderRadius: "2px" }}
                                                                onClick={handleShowResetModal}
                                                            >
                                                                <i className="fa fa-refresh mr-2" aria-hidden="true"></i>
                                                                Adresi Resetle
                                                            </Button>}
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            className='mt-2'
                                                            onClick={() => handleInfoClick('resetButton')}
                                                            style={{
                                                                right: '0',
                                                                padding: '2px 6px',
                                                                color: '#007bff',
                                                                fontSize: '12px',
                                                                backgroundColor: 'white',
                                                                border: '1px solid #007bff',
                                                                borderRadius: '3px',
                                                                height: '-webkit-fill-available'

                                                            }}
                                                        >
                                                            <i className="fa fa-question-circle-o"></i>
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {loading && (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Yükleniyor...</span>
                                        </div>
                                        <div style={{ marginTop: '10px' }}>Yükleniyor...</div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Reset Modal - Ayrı render */}
            <ResetAddressModal
                isOpen={showResetModal}
                onClose={handleCancelReset}
                onConfirm={handleReset}
                selectedAddress={selectedAddress}
            />

            {/* Info Modal */}
            <Modal isOpen={showInfoModal} toggle={() => setShowInfoModal(false)} size="md" centered>
                <ModalHeader toggle={() => setShowInfoModal(false)} style={{ backgroundColor: '#007bff', color: 'white' }}>
                    <i className="fa fa-info-circle mr-2"></i>
                    {getInfoContent().title}
                </ModalHeader>
                <ModalBody style={{ padding: '20px' }}>
                    {getInfoContent().content}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setShowInfoModal(false)}>
                        <i className="fa fa-check mr-2"></i>
                        Anladım
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default SayimAdresSecimi;